import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Typography,
  Grid,
  Card,
  Tooltip,
  Box,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import SearchIcon from '@mui/icons-material/Search';
import CustomCard from '../../components/card/CustomCard';
import AppWidgetSummary from '../../sections/@dashboard/app/AppWidgetSummary';
import {
  addProjectComment,
  deleteProjectComment,
  getProjectDashboardSummary,
  getProjectSummaryDetails,
  updateProjectComment,
} from '../../services/projectService';
import {
  calculateProjectHealth,
  getCurrentSprint,
  getLatestTaskInCurrentSprint,
  truncateText,
} from '../../utils/projectHealth';
import formatDate from '../../utils/formatDate';
import AddComment from './details/components/AddComment';
import DeleteModal from '../teams/components/DeleteModal';
import { isAdmin } from '../../utils/share';

export default function ProjectSummaryDashboard() {
  const accountData = useSelector((state) => state?.login?.user);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);

  // MAIN COMMENT MODAL (Add new comment)
  const [commentModal, setCommentModal] = useState({
    open: false,
    projectId: null,
  });

  // MENU / EDIT / DELETE STATES
  const [selectedComment, setSelectedComment] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [search, setSearch] = useState('');

  const navigate = useNavigate();

  const openCommentModal = (projectId) => {
    setCommentModal({ open: true, projectId });
  };

  const closeCommentModal = () => {
    setCommentModal({ open: false, projectId: null });
  };

  const refresh = async () => {
    const res = await getProjectDashboardSummary();
    setProjects(res.data.data.projects);
  };
  useEffect(() => {
    const fetchSummaryDeatils = async () => {
      setProjectsLoading(true);
      const res = await getProjectSummaryDetails(search);
      console.log(res);
      setProjects(res.data.data);
      setProjectsLoading(false);
    };
    fetchSummaryDeatils();
  }, [search]);

  const submitComment = async (text) => {
    setCommentLoading(true);

    const res = await addProjectComment({ comment: text }, commentModal.projectId);

    // backend response
    const backendComment = res.data.data;

    const newComment = {
      ...backendComment,
      projectId: commentModal.projectId, // <--- Required for delete
      commentAuthor: {
        id: accountData.id,
        userName: accountData.userName,
      },
    };

    closeCommentModal();
    setCommentLoading(false);

    setProjects((prev) =>
      prev.map((p) => (p.id === commentModal.projectId ? { ...p, comments: [newComment, ...p.comments] } : p))
    );
  };

  const updateComment = async (text) => {
    if (!selectedComment) return;

    const res = await updateProjectComment(selectedComment.id, { comment: text });

    const updated = {
      ...res.data.data,
      projectId: selectedComment.projectId,
      commentAuthor: selectedComment.commentAuthor,
    };

    setEditModalOpen(false);

    setProjects((prev) =>
      prev.map((p) =>
        p.id === updated.projectId
          ? {
              ...p,
              comments: p.comments.map((c) => (c.id === updated.id ? updated : c)),
            }
          : p
      )
    );
  };

  const removeComment = async () => {
    if (!selectedComment) return;

    await deleteProjectComment(selectedComment.id);

    const pid = selectedComment.projectId;

    setProjects((prev) =>
      prev.map((p) => (p.id === pid ? { ...p, comments: p.comments.filter((c) => c.id !== selectedComment.id) } : p))
    );

    setDeleteModalOpen(false);
  };
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearch(value);
    }, 300),
    []
  );
  useEffect(() => {
    const fetchProjectSummary = async () => {
      setLoading(true);
      const res = await getProjectDashboardSummary();
      setData(res.data.data);
      setLoading(false);
    };
    fetchProjectSummary();
  }, []);

  return (
    <>
      <Helmet>
        <title>Project Dashboard</title>
      </Helmet>

      <Typography variant="h6" gutterBottom>
        Project Dashboard
      </Typography>

      {/* ====================== TOP CARDS ====================== */}
      <Grid container spacing={4} pl={2} pr={2}>
        <Grid item container xs={12} spacing={4}>
          <Grid container item spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <CustomCard
                value={data?.totalProjects ?? 'N/A'}
                label="Total Projects"
                bgColor="linear-gradient(to right, #FFA726 0%, rgba(255,167,38,0) 100%)"
                iconURL="/assets/icons/project-43.svg"
                loading={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <CustomCard
                value={data?.onTrackProjects ?? 'N/A'}
                label="On Track Projects"
                bgColor="linear-gradient(135deg, #22C55E 0%, #065E49 100%)"
                iconURL="/assets/icons/track-on-track.svg"
                loading={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <CustomCard
                value={data?.delayedProjects ?? 'N/A'}
                label="Delayed Project"
                bgColor="linear-gradient(135deg, #8E33FF 0%, #5119B7 100%)"
                iconURL="/assets/icons/delayed-3.svg"
                loading={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <CustomCard
                value={data?.inDevelopmentProjects ?? 'N/A'}
                label="In Development"
                bgColor="linear-gradient(135deg, #2289c5 0%, #06355e 100%)"
                iconURL="/assets/icons/in-development.svg"
                loading={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <CustomCard
                value={data?.uatProjects ?? 'N/A'}
                label="In UAT"
                bgColor="linear-gradient(135deg, #f13030 0%, #bb1313 100%)"
                iconURL="/assets/icons/quality-testing.svg"
                loading={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <CustomCard
                value={data?.liveProjects ?? 'N/A'}
                label="Live"
                bgColor="linear-gradient(135deg, #803D3B 0%, #803D3B 100%)"
                iconURL="/assets/icons/live-broadcast-4.svg"
                loading={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <CustomCard
                value={data?.overBudgetProjects ?? 'N/A'}
                label="Over Budget"
                bgColor="linear-gradient(135deg, #203D3B 0%, #153D3B 100%)"
                iconURL="/assets/icons/over-budget.svg"
                loading={loading}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* ====================== ALL PROJECTS ====================== */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" sx={{ mt: 4, px: 2 }}>
          Projects
        </Typography>
        {isAdmin() && (
          <TextField
            id="outlined-basic"
            label="Search Project"
            variant="outlined"
            size="small"
            onChange={(e) => debouncedSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        )}
      </Box>

      {projectsLoading ? (
        <CircularProgress style={{ display: 'block', margin: '20px auto' }} />
      ) : (
        projects?.map((p) => {
          const latest = getLatestTaskInCurrentSprint(p.sprints);
          const latestText = latest?.taskName || 'No Task';

          return (
            <Card key={p.id} sx={{ p: 3, m: 2 }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{ mb: 2, fontWeight: 600, cursor: 'pointer' }}
                onClick={() => navigate(`/project/overview/${p.id}`)}
              >
                {p.projectName}
              </Typography>

              {/* ========= WIDGET SECTION ========= */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={5} lg={2.4}>
                  <AppWidgetSummary
                    title="Current Status"
                    total={calculateProjectHealth(p, p?.sprints).health}
                    color="success"
                    sx={{
                      '& .MuiTypography-h4': {
                        fontSize: '1.25rem',
                        fontWeight: 600,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3} lg={2.4}>
                  <AppWidgetSummary
                    title="Risk Status"
                    total={calculateProjectHealth(p, p?.sprints).risk}
                    color="warning"
                    sx={{
                      '& .MuiTypography-h4': {
                        fontSize: '1.25rem',
                        fontWeight: 600,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3} lg={2.4}>
                  <AppWidgetSummary
                    title="Current Sprint"
                    total={getCurrentSprint(p?.sprints)?.name ?? 'No Current Sprint'}
                    color="info"
                    sx={{
                      '& .MuiTypography-h4': {
                        fontSize: '1.25rem',
                        fontWeight: 600,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3} lg={2.4}>
                  <AppWidgetSummary
                    title="Planned UAT Date"
                    total={formatDate(p.projectUATDate)}
                    color="primary"
                    sx={{
                      '& .MuiTypography-h4': {
                        fontSize: '1.25rem',
                        fontWeight: 600,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3} lg={2.4}>
                  <AppWidgetSummary
                    title="Planned Release Date"
                    total={formatDate(p.ProjectLiveDate)}
                    color="primary"
                    sx={{
                      '& .MuiTypography-h4': {
                        fontSize: '1.25rem',
                        fontWeight: 600,
                      },
                    }}
                  />
                </Grid>
                {/* ========= LATEST TASK SECTION ========= */}
                <Grid item xs={12}>
                  <Card
                    sx={{
                      p: 1,
                      background: '#ffffff',
                      borderRadius: 1.5,
                      border: '#e0e0e0',
                      mb: 0.5,
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Typography variant="h6" fontWeight={500}>
                        Latest Task
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 16,
                          fontWeight: 500,
                          mt: 0.5,
                          color: '#555',
                          display: '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {latestText}
                      </Typography>
                    </Box>
                    {/* GET ALL TASKS BUTTON */}
                    <Box sx={{ mt: 1.5 }}>
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.6,
                          background: '#0288d120',
                          borderRadius: 1,
                          border: '1px solid #0288d1',
                          cursor: 'pointer',
                          width: 'fit-content',
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#0288d1',
                        }}
                        onClick={() => navigate(`/project/details/${p.id}`)}
                      >
                        Get All Tasks
                      </Box>
                    </Box>
                  </Card>
                </Grid>

                {/* ========= COMMENTS SECTION ========= */}
                <Grid item xs={12}>
                  <Card sx={{ p: 1, background: '#ffffff' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" fontWeight={500}>
                        Comments
                      </Typography>
                      <Box
                        sx={{
                          mt: 1.5,
                          px: 1.5,
                          py: 0.6,
                          background: '#0288d120',
                          borderRadius: 1,
                          border: '1px dashed #0288d1',
                          cursor: 'pointer',
                          width: 'fit-content',
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#0288d1',
                        }}
                        onClick={() => openCommentModal(p.id)}
                      >
                        + Add Comment
                      </Box>
                    </Box>

                    {(() => {
                      const comments = p.comments ?? [];
                      const showMore = p.showComments ?? false;
                      const visible = showMore ? comments : comments.slice(0, 1);

                      return (
                        <Box sx={{ mt: 1 }}>
                          {visible.length > 0 ? (
                            visible.map((c) => (
                              <Box
                                key={c.id}
                                sx={{
                                  mb: 1.5,
                                  pb: 1,
                                  borderBottom: '1px solid #ddd',
                                  position: 'relative',
                                }}
                              >
                                {/* edit menu only for owner */}
                                {accountData.id === c?.commentAuthor?.id && (
                                  <IconButton
                                    size="small"
                                    sx={{ position: 'absolute', top: 0, right: 0 }}
                                    onClick={(e) => {
                                      setSelectedComment(c);
                                      setMenuAnchor(e.currentTarget);
                                    }}
                                  >
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                )}

                                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{c.comment}</Typography>

                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    color: '#777',
                                    mt: 0.5,
                                    display: 'flex',
                                    gap: 1,
                                  }}
                                >
                                  {c?.commentAuthor?.userName}
                                  <span>{formatDate(c?.createdAt)}</span>
                                </Typography>
                              </Box>
                            ))
                          ) : (
                            <Typography sx={{ fontSize: 13, color: '#999' }}>No comments yet.</Typography>
                          )}

                          {/* Show more / show less */}
                          {comments.length > 1 && (
                            <Typography
                              sx={{
                                fontSize: 13,
                                mt: 0.5,
                                cursor: 'pointer',
                                fontWeight: 600,
                                color: '#0288d1',
                              }}
                              onClick={() =>
                                setProjects((prev) =>
                                  prev.map((proj) =>
                                    proj.id === p.id ? { ...proj, showComments: !p.showComments } : proj
                                  )
                                )
                              }
                            >
                              {showMore ? 'Show less' : `Show more comment...`}
                            </Typography>
                          )}

                          {/* Add comment */}
                        </Box>
                      );
                    })()}
                  </Card>
                </Grid>
              </Grid>
            </Card>
          );
        })
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            setEditModalOpen(true);
            setMenuAnchor(null);
          }}
          disabled={!selectedComment}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteModalOpen(true);
            setMenuAnchor(null);
          }}
          disabled={!selectedComment}
        >
          Delete
        </MenuItem>
      </Menu>

      <AddComment open={commentModal.open} onClose={closeCommentModal} onSubmit={submitComment} />

      <AddComment
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        initialValue={selectedComment?.comment}
        onSubmit={updateComment}
      />

      <DeleteModal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} onDelete={removeComment} />
    </>
  );
}
